import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppBar, Toolbar, Typography, Button, Box, Container, Drawer, List, ListItem, ListItemIcon, ListItemText, Avatar, Menu, MenuItem, Divider, IconButton, Tooltip, Badge, CircularProgress } from '@mui/material';
import { Menu as MenuIcon, Home as HomeIcon, Person as PersonIcon, ExitToApp as LogoutIcon, Settings as SettingsIcon, Notifications as NotificationsIcon, Business as BusinessIcon } from '@mui/icons-material';
import * as api from '../../api';
const ClientPortalLayout = ({
  children,
  modules = [],
  currentModule = null
}) => {
  const {
    i18n,
    t
  } = useTranslation("crm");

  // Helper function to normalize language code (e.g., en-US -> en)
  const history = useHistory();
  const location = useLocation();
  const normalizeLanguage = lang => {
    if (!lang) return 'en';
    return lang.split('-')[0]; // Get only the base language code
  };
  const currentLang = normalizeLanguage(i18n.language);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);

  // Load client config on mount
  useEffect(() => {
    // Ensure API token is initialized from localStorage
    try {
      const profile = localStorage.getItem('profile');
      if (profile) {
        const userData = JSON.parse(profile);
        if (userData.token) {
          api.updateAuthToken(userData.token);
        }
      }
    } catch (error) {
      console.error('Error initializing auth token:', error);
    }
    loadClientConfig();
    loadNotifications();
  }, []);
  const loadClientConfig = async () => {
    setLoading(true);
    try {
      const {
        data
      } = await api.fetchClientPortalConfig();
      setClientInfo(data.data);
    } catch (error) {
      console.error('Error loading client config:', error);
      // If authentication fails, redirect to login
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };
  const loadNotifications = async () => {
    try {
      const {
        data
      } = await api.fetchClientPortalNotifications();
      setNotifications(data.data?.notifications || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };
  const handleLogout = () => {
    localStorage.removeItem('profile');
    const normalizedLang = normalizeLanguage(i18n.language);
    history.push(`/${normalizedLang}/client-login`);
  };
  const handleProfileMenuOpen = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };
  const handleNotificationOpen = event => {
    setNotificationAnchor(event.currentTarget);
  };
  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };
  const navigateTo = path => {
    history.push(`/${currentLang}${path}`);
    setDrawerOpen(false);
  };
  const unreadCount = notifications.filter(n => !n.read).length;
  if (loading) {
    return <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh'
    }}>
        <CircularProgress size={60} />
      </Box>;
  }
  return <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    bgcolor: 'grey.50'
  }}>
      {/* Top App Bar */}
      <AppBar position="sticky" elevation={1}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => setDrawerOpen(true)} sx={{
          mr: 2
        }}>
            <MenuIcon />
          </IconButton>

          <BusinessIcon sx={{
          mr: 1,
          fontSize: 28
        }} />
          <Typography variant="h6" sx={{
          flexGrow: 1
        }}>
            {t('client_portal') || 'Client Portal'}
          </Typography>

          {/* Notifications */}
          <Tooltip title={t('notifications') || 'Notifications'}>
            <IconButton color="inherit" onClick={handleNotificationOpen}>
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Profile Menu */}
          <Button color="inherit" onClick={handleProfileMenuOpen} sx={{
          textTransform: 'none'
        }}>
            <Avatar sx={{
            width: 32,
            height: 32,
            mr: 1,
            bgcolor: 'secondary.main'
          }} src={clientInfo?.client?.profileImage}>
              {clientInfo?.client?.name?.charAt(0) || 'C'}
            </Avatar>
            <Box sx={{
            textAlign: 'left',
            display: {
              xs: 'none',
              sm: 'block'
            }
          }}>
              <Typography variant="body2" sx={{
              lineHeight: 1.2
            }}>
                {clientInfo?.client?.name}
              </Typography>
              <Typography variant="caption" sx={{
              opacity: 0.8,
              lineHeight: 1
            }}>
                {clientInfo?.client?.clientId}
              </Typography>
            </Box>
          </Button>
        </Toolbar>
      </AppBar>

      {/* Side Drawer */}
      <Drawer anchor={currentLang === 'ar' ? 'right' : 'left'} open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{
      sx: {
        width: 280
      }
    }} ModalProps={{
      keepMounted: true,
      container: document.body
    }} SlideProps={{
      direction: currentLang === 'ar' ? 'left' : 'right'
    }}>
        <Box sx={{
        pt: 2,
        pb: 2
      }}>
          {/* Header */}
          <Box sx={{
          px: 2,
          mb: 2
        }}>
            <Box display="flex" alignItems="center" mb={1}>
              <Avatar sx={{
              width: 48,
              height: 48,
              mr: 2
            }} src={clientInfo?.client?.profileImage}>
                {clientInfo?.client?.name?.charAt(0) || 'C'}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {clientInfo?.client?.name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {clientInfo?.client?.email}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider />

          <List>
            {/* Home */}
            <ListItem button onClick={() => navigateTo('/client-portal/home')} selected={location.pathname.includes('/client-portal/home')}>
              <ListItemIcon>
                <HomeIcon color={location.pathname.includes('/client-portal/home') ? 'primary' : 'inherit'} />
              </ListItemIcon>
              <ListItemText primary={t('home') || 'Home'} />
            </ListItem>

            <Divider sx={{
            my: 1
          }} />

            {/* Module Menu Items */}
            {modules && modules.length > 0 && <>
                <ListItem sx={{
              pt: 1,
              pb: 1
            }}>
                  <Typography variant="caption" color="textSecondary" fontWeight="bold">
                    {t('my_services') || 'MY SERVICES'}
                  </Typography>
                </ListItem>
                {modules.map(module => <ListItem button key={module.name} onClick={() => navigateTo(module.path)} selected={location.pathname.includes(module.path)}>
                    <ListItemIcon>
                      {module.icon && React.cloneElement(module.icon, {
                  color: location.pathname.includes(module.path) ? 'primary' : t("inherit")
                })}
                    </ListItemIcon>
                    <ListItemText primary={module.label} />
                  </ListItem>)}
                <Divider sx={{
              my: 1
            }} />
              </>}

            {/* Settings */}
            <ListItem button onClick={() => navigateTo('/client-portal/profile')} selected={location.pathname.includes('/client-portal/profile')}>
              <ListItemIcon>
                <PersonIcon color={location.pathname.includes('/client-portal/profile') ? 'primary' : 'inherit'} />
              </ListItemIcon>
              <ListItemText primary={t('my_profile') || 'My Profile'} />
            </ListItem>

            <ListItem button onClick={() => navigateTo('/client-portal/settings')} selected={location.pathname.includes('/client-portal/settings')}>
              <ListItemIcon>
                <SettingsIcon color={location.pathname.includes('/client-portal/settings') ? 'primary' : 'inherit'} />
              </ListItemIcon>
              <ListItemText primary={t('settings') || 'Settings'} />
            </ListItem>

            <Divider sx={{
            my: 1
          }} />

            {/* Logout */}
            <ListItem button onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary={t('logout') || 'Logout'} />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Profile Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleProfileMenuClose} transformOrigin={{
      horizontal: 'right',
      vertical: 'top'
    }} anchorOrigin={{
      horizontal: 'right',
      vertical: 'bottom'
    }}>
        <MenuItem onClick={() => {
        navigateTo('/client-portal/profile');
        handleProfileMenuClose();
      }}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('my_profile') || 'My Profile'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
        navigateTo('/client-portal/settings');
        handleProfileMenuClose();
      }}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('settings') || 'Settings'}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('logout') || 'Logout'}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Notifications Menu */}
      <Menu anchorEl={notificationAnchor} open={Boolean(notificationAnchor)} onClose={handleNotificationClose} transformOrigin={{
      horizontal: 'right',
      vertical: 'top'
    }} anchorOrigin={{
      horizontal: 'right',
      vertical: 'bottom'
    }} PaperProps={{
      sx: {
        width: 320,
        maxHeight: 400
      }
    }}>
        <Box sx={{
        px: 2,
        py: 1
      }}>
          <Typography variant="h6">{t('notifications') || 'Notifications'}</Typography>
        </Box>
        <Divider />
        {notifications.length === 0 ? <MenuItem disabled>
            <Typography variant="body2" color="textSecondary">
              {t('no_notifications') || 'No notifications'}
            </Typography>
          </MenuItem> : notifications.slice(0, 5).map((notification, index) => <MenuItem key={index} onClick={handleNotificationClose}>
              <ListItemText primary={notification.title} secondary={notification.message} primaryTypographyProps={{
          variant: 'body2',
          fontWeight: notification.read ? 'normal' : 'bold'
        }} />
            </MenuItem>)}
        {notifications.length > 5 && <>
            <Divider />
            <MenuItem onClick={() => {
          navigateTo('/client-portal/notifications');
          handleNotificationClose();
        }}>
              <Typography variant="body2" color="primary" align="center" sx={{
            width: '100%'
          }}>
                {t('view_all') || 'View all'}
              </Typography>
            </MenuItem>
          </>}
      </Menu>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{
      flexGrow: 1,
      py: 3
    }}>
        {children}
      </Container>

      {/* Footer */}
      <Box component="footer" sx={{
      bgcolor: 'white',
      py: 3,
      borderTop: 1,
      borderColor: 'divider',
      mt: 'auto'
    }}>
        <Container maxWidth="xl">
          <Typography variant="body2" color="textSecondary" align="center">
            © {new Date().getFullYear()} {clientInfo?.client?.name || 'Quantum Billing'}. {t('all_rights_reserved') || 'All rights reserved.'}
          </Typography>
          <Typography variant="caption" color="textSecondary" align="center" display="block" sx={{
          mt: 0.5
        }}>
            {t('powered_by') || 'Powered by'}{t("Quantum Billing System")}</Typography>
        </Container>
      </Box>
    </Box>;
};
export default ClientPortalLayout;
